import { MigrationEnv } from "../../generated/graphql";

/** Human-readable label for each migration environment. */
export const ENV_LABELS: Record<MigrationEnv, string> = {
  [MigrationEnv.Production]: "Production",
  [MigrationEnv.Release]: "Release",
  [MigrationEnv.Staging]: "Staging",
  [MigrationEnv.Development]: "Development",
  [MigrationEnv.Localdev]: "Local dev",
  [MigrationEnv.Demo]: "Demo",
};

/**
 * Environments selectable as a migration target. `readonly` because this is a
 * module-level singleton shared by every render: a consumer calling `.sort()` on
 * it would reorder the picker app-wide.
 */
export const TARGET_ENV_OPTIONS: readonly MigrationEnv[] =
  Object.values(MigrationEnv);

/**
 * Environments selectable as a migration source. Demo is excluded: it is a
 * disposable write target only and may never be a migration source (the backend
 * rejects it, this hides it from the picker). Mirrors the backend source-lock in
 * `ConfigMigrationService`.
 *
 * Derived from `MigrationEnv` directly rather than from `TARGET_ENV_OPTIONS`, so
 * a future restriction on the target list cannot silently narrow the source list
 * as well.
 */
export const SOURCE_ENV_OPTIONS: readonly MigrationEnv[] = Object.values(
  MigrationEnv,
).filter((env) => env !== MigrationEnv.Demo);

/**
 * Targets whose writes the operator must confirm. Production is live customer
 * data; demo is a persistent, externally visible deployment a prospect may be
 * looking at. Mirrors `targetRequiresConfirmation` in the backend service, which
 * is the enforcing copy.
 */
const CONFIRMED_WRITE_TARGETS: ReadonlySet<MigrationEnv> = new Set([
  MigrationEnv.Production,
  MigrationEnv.Demo,
]);

/**
 * Whether a non-dry-run write to this target needs explicit acknowledgement.
 *
 * @param target The selected target environment.
 * @param dryRun Whether the run is a preview.
 * @returns True when the confirmation checkbox must be ticked before submitting.
 */
export const requiresWriteConfirmation = (
  target: MigrationEnv,
  dryRun: boolean,
): boolean => !dryRun && CONFIRMED_WRITE_TARGETS.has(target);

/**
 * Whether the operator's acknowledgement covers the currently selected target.
 *
 * The acknowledgement records WHICH target was confirmed rather than a bare
 * boolean. With two confirmation-gated targets, a boolean survives a target
 * change: ticking the box for production, switching to demo and submitting sent
 * a confirmed flag for an environment nobody acknowledged, and the backend
 * cannot tell the difference because the field only says "confirmed", not
 * "confirmed for what".
 *
 * @param target The selected target environment, or "" when none is selected.
 * @param confirmedTarget The target the operator ticked the box for, if any.
 * @returns True when the selection may be submitted as confirmed.
 */
export const isWriteConfirmed = (
  target: MigrationEnv | "",
  confirmedTarget: MigrationEnv | null,
): boolean => target !== "" && confirmedTarget === target;
